import React, { useEffect, useState } from "react";
import api, { API_BASE } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import { toast } from "sonner";
import { Plus, KeyRound, Copy, Trash2, Download, ExternalLink, ShieldCheck } from "lucide-react";
import { formatDateTime } from "../lib/format";

const ROLE_OPTIONS = [
  { value: "admin", label: "Administrator (full access)" },
  { value: "credit_officer", label: "Credit Officer (CRUD borrower/facility/collateral)" },
  { value: "risk_officer", label: "Risk Officer (read + resolve alerts)" },
  { value: "approver", label: "Approver (decide approval requests)" },
  { value: "viewer", label: "Viewer (read-only)" },
];

const CATEGORY_COLORS = {
  Borrower: "bg-blue-50 text-blue-800 border-blue-200",
  "Group Obligor": "bg-zinc-100 text-zinc-800 border-zinc-300",
  Facility: "bg-zinc-100 text-zinc-800 border-zinc-300",
  Exposure: "bg-purple-50 text-purple-800 border-purple-200",
  Collateral: "bg-emerald-50 text-emerald-800 border-emerald-200",
  Guarantor: "bg-amber-50 text-amber-800 border-amber-200",
  Covenant: "bg-zinc-100 text-zinc-700 border-zinc-300",
  Approval: "bg-indigo-50 text-indigo-800 border-indigo-200",
  Alert: "bg-red-50 text-red-800 border-red-200",
  Dashboard: "bg-zinc-100 text-zinc-700 border-zinc-300",
  Graph: "bg-zinc-100 text-zinc-700 border-zinc-300",
  Report: "bg-zinc-100 text-zinc-700 border-zinc-300",
};

const METHOD_COLORS = {
  GET: "bg-emerald-700 text-white",
  POST: "bg-[#0055FF] text-white",
  PUT: "bg-[#F5A623] text-black",
  DELETE: "bg-[#D0021B] text-white",
};

export default function Integrations() {
  const [keys, setKeys] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [form, setForm] = useState({ name: "", role: "credit_officer", rate_limit_per_min: 100, description: "" });
  const [activeWorkflow, setActiveWorkflow] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [k, c] = await Promise.all([
        api.get("/integrations/api-keys"),
        api.get("/integrations/catalog"),
      ]);
      setKeys(k.data);
      setCatalog(c.data);
      if (c.data?.workflows?.length) setActiveWorkflow(c.data.workflows[0].id);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- load once on mount

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error("Nama API key wajib diisi");
      return;
    }
    try {
      const res = await api.post("/integrations/api-keys", {
        ...form,
        rate_limit_per_min: parseInt(form.rate_limit_per_min, 10) || 100,
      });
      setNewKey(res.data);
      setCreateOpen(false);
      setForm({ name: "", role: "credit_officer", rate_limit_per_min: 100, description: "" });
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal membuat key");
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm("Revoke API key ini? Sistem yang sedang pakai akan langsung 401.")) return;
    try {
      await api.delete(`/integrations/api-keys/${id}`);
      toast.success("API key di-revoke");
      loadAll();
    } catch {
      toast.error("Gagal revoke key");
    }
  };

  const handleDownloadPostman = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const res = await fetch(`${API_BASE}/integrations/postman`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error("fail");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "limit-graph-postman.json";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Postman collection diunduh");
    } catch {
      toast.error("Gagal mengunduh Postman collection");
    }
  };

  const copyToClipboard = (text, label = "") => {
    navigator.clipboard.writeText(text);
    toast.success(`${label || "Disalin"}`);
  };

  const wf = catalog?.workflows?.find((w) => w.id === activeWorkflow);

  return (
    <div className="p-8 max-w-[1600px] mx-auto" data-testid="integrations-page">
      <PageHeader
        title="Integrasi API"
        subtitle="Kelola API key, lihat katalog endpoint, download Postman collection, dan workflow integrasi."
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleDownloadPostman} data-testid="download-postman-btn">
              <Download className="h-3.5 w-3.5 mr-1.5" /> Postman Collection
            </Button>
            <a href="/api/docs" target="_blank" rel="noreferrer">
              <Button variant="outline">
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Swagger UI
              </Button>
            </a>
            <Button onClick={() => setCreateOpen(true)} data-testid="create-api-key-btn">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Buat API Key
            </Button>
          </div>
        }
      />

      <Tabs defaultValue="keys" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="keys" data-testid="tab-keys">API Keys</TabsTrigger>
          <TabsTrigger value="quickstart" data-testid="tab-quickstart">Quick Start</TabsTrigger>
          <TabsTrigger value="workflows" data-testid="tab-workflows">Workflows</TabsTrigger>
          <TabsTrigger value="endpoints" data-testid="tab-endpoints">Endpoint Catalog</TabsTrigger>
        </TabsList>

        {/* ===== Keys tab ===== */}
        <TabsContent value="keys">
          <div className="border border-border rounded-md bg-card overflow-hidden">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider">API Keys</h3>
                <p className="text-xs text-muted-foreground mt-0.5">{keys.length} key terdaftar</p>
              </div>
            </div>
            {loading ? (
              <div className="p-6 text-sm text-muted-foreground">Memuat…</div>
            ) : keys.length === 0 ? (
              <div className="p-10 text-center">
                <KeyRound className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Belum ada API key. Klik "Buat API Key" untuk mulai.</p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nama</th>
                    <th>Prefix</th>
                    <th>Role</th>
                    <th className="text-right">Rate Limit</th>
                    <th className="text-right">Usage</th>
                    <th>Last Used</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map((k) => (
                    <tr key={k.id} data-testid={`api-key-row-${k.id}`}>
                      <td>
                        <div className="font-medium">{k.name}</div>
                        {k.description && <div className="text-xs text-muted-foreground">{k.description}</div>}
                      </td>
                      <td><span className="mono text-xs">{k.prefix}***</span></td>
                      <td><Badge variant="outline">{k.role}</Badge></td>
                      <td className="text-right-num">{k.rate_limit_per_min}/min</td>
                      <td className="text-right-num">{k.usage_count?.toLocaleString("id-ID") || 0}</td>
                      <td className="mono text-xs">{k.last_used_at ? formatDateTime(k.last_used_at) : "—"}</td>
                      <td>
                        {k.status === "active" ? (
                          <Badge className="bg-emerald-700 text-white">active</Badge>
                        ) : (
                          <Badge variant="secondary">revoked</Badge>
                        )}
                      </td>
                      <td className="mono text-xs">{formatDateTime(k.created_at)}</td>
                      <td>
                        {k.status === "active" && (
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => handleRevoke(k.id)}
                            data-testid={`revoke-${k.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-[#D0021B]" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        {/* ===== Quick Start tab ===== */}
        <TabsContent value="quickstart">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="border border-border rounded-md bg-card p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 bg-foreground text-background rounded-full flex items-center justify-center font-bold mono text-sm">1</div>
                <h3 className="font-bold">Generate API Key</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Klik "Buat API Key", tentukan nama, role, dan rate limit. Salin raw key — akan ditampilkan sekali.
              </p>
              <Button size="sm" onClick={() => setCreateOpen(true)} variant="outline">
                Buat sekarang →
              </Button>
            </div>

            <div className="border border-border rounded-md bg-card p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 bg-foreground text-background rounded-full flex items-center justify-center font-bold mono text-sm">2</div>
                <h3 className="font-bold">Set Header</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Tambahkan header pada setiap request:
              </p>
              <pre className="bg-zinc-950 text-zinc-100 text-xs p-3 rounded mono overflow-x-auto">{`X-API-Key: llf_xxxxxxxxxxx`}</pre>
            </div>

            <div className="border border-border rounded-md bg-card p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 bg-foreground text-background rounded-full flex items-center justify-center font-bold mono text-sm">3</div>
                <h3 className="font-bold">Test Endpoint</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Test koneksi via cURL atau Postman:
              </p>
              <pre className="bg-zinc-950 text-zinc-100 text-[10px] p-3 rounded mono overflow-x-auto">{`curl -H "X-API-Key: $KEY" \\
  ${window.location.origin}/api/dashboard/summary`}</pre>
            </div>
          </div>

          <div className="mt-6 border border-border rounded-md bg-card p-5">
            <h3 className="font-bold mb-3 flex items-center gap-2"><ShieldCheck className="h-4 w-4" />Keamanan & Best Practice</h3>
            <ul className="text-sm space-y-2 text-muted-foreground list-disc pl-5">
              <li>API key bersifat <b>rahasia</b>. Simpan di environment variable / secret manager. Jangan commit ke Git.</li>
              <li>Buat key per <b>sistem konsumen</b> (Core Banking, Risk Engine, BI) agar bisa di-revoke independen.</li>
              <li>Pilih <b>role minimal</b> yang dibutuhkan. Contoh: Sync utilization → <span className="mono">credit_officer</span>; BI dashboard → <span className="mono">viewer</span>.</li>
              <li>Set <b>rate limit</b> sesuai kebutuhan. Default 100/menit. Hubungi admin jika perlu lebih tinggi.</li>
              <li>Server membalas <span className="mono">429 Too Many Requests</span> dengan header <span className="mono">Retry-After</span> jika limit terlampaui.</li>
              <li>Audit trail: semua mutasi via API tercatat di <span className="mono">audit_logs</span> dengan actor <span className="mono">apikey:&lt;nama&gt;</span>.</li>
            </ul>
          </div>
        </TabsContent>

        {/* ===== Workflows tab ===== */}
        <TabsContent value="workflows">
          {catalog?.workflows && (
            <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
              <aside className="space-y-1">
                {catalog.workflows.map((w) => (
                  <button
                    key={w.id}
                    onClick={() => setActiveWorkflow(w.id)}
                    className={`w-full text-left px-3 py-2.5 rounded-md text-sm border transition-colors ${
                      activeWorkflow === w.id
                        ? "border-foreground bg-foreground/5 font-semibold"
                        : "border-border hover:border-foreground/30"
                    }`}
                    data-testid={`workflow-tab-${w.id}`}
                  >
                    {w.title}
                  </button>
                ))}
              </aside>

              <div className="border border-border rounded-md bg-card p-6">
                {wf && (
                  <>
                    <h2 className="text-xl font-bold tracking-tight">{wf.title}</h2>
                    <p className="text-sm text-muted-foreground mt-1 mb-4">{wf.description}</p>

                    <h4 className="text-xs uppercase tracking-wider text-muted-foreground mt-4 mb-2">Endpoint Sequence</h4>
                    <div className="space-y-2">
                      {wf.steps.map((s) => (
                        <div key={`${s.method}-${s.path}`} className="flex items-start gap-2 text-sm border border-border rounded p-2.5">
                          <span className={`mono text-[10px] px-2 py-0.5 rounded font-bold ${METHOD_COLORS[s.method]}`}>{s.method}</span>
                          <div className="flex-1">
                            <div className="mono text-xs">{s.path}</div>
                            <div className="text-xs text-muted-foreground">{s.purpose}</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {wf.example_body && (
                      <>
                        <div className="flex items-center justify-between mt-5 mb-2">
                          <h4 className="text-xs uppercase tracking-wider text-muted-foreground">Contoh Request Body</h4>
                          <Button size="sm" variant="ghost" onClick={() => copyToClipboard(JSON.stringify(wf.example_body, null, 2), "Body disalin")}>
                            <Copy className="h-3 w-3 mr-1" /> Copy
                          </Button>
                        </div>
                        <pre className="bg-zinc-950 text-zinc-100 text-xs p-4 rounded mono overflow-x-auto">
{JSON.stringify(wf.example_body, null, 2)}
                        </pre>
                      </>
                    )}

                    <h4 className="text-xs uppercase tracking-wider text-muted-foreground mt-5 mb-2">Contoh cURL</h4>
                    <pre className="bg-zinc-950 text-zinc-100 text-xs p-4 rounded mono overflow-x-auto">
{`curl -X ${wf.steps[0].method} \\
  ${window.location.origin}${wf.steps[0].path.replace("{borrower_id}", "BRW-EXT-0001").replace("{facility_id}", "FAC-0001").replace("{alert_id}", "ALT-XXXXXXXX").replace("{approval_id}", "APR-XXXX")} \\
  -H "X-API-Key: llf_YOUR_KEY_HERE"${wf.example_body ? ` \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(wf.example_body)}'` : ""}`}
                    </pre>
                  </>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        {/* ===== Endpoints tab ===== */}
        <TabsContent value="endpoints">
          <div className="border border-border rounded-md bg-card overflow-hidden">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider">Endpoint Catalog</h3>
                <p className="text-xs text-muted-foreground mt-0.5">{catalog?.endpoints?.length || 0} endpoint tersedia</p>
              </div>
              <div className="text-xs text-muted-foreground mono">
                Base URL: <span className="font-semibold">{window.location.origin}{catalog?.base_url || "/api"}</span>
              </div>
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Method</th>
                  <th>Path</th>
                  <th>Kategori</th>
                  <th>Min. Role</th>
                  <th>cURL</th>
                </tr>
              </thead>
              <tbody>
                {(catalog?.endpoints || []).map((ep) => {
                  const url = `${window.location.origin}${ep.path}`;
                  const curl = `curl -X ${ep.method} "${url}" -H "X-API-Key: $KEY"${ep.method !== "GET" ? ` -H "Content-Type: application/json" -d '{}'` : ""}`;
                  return (
                    <tr key={`${ep.method}-${ep.path}`} data-testid={`endpoint-${ep.method}-${ep.path}`}>
                      <td>
                        <span className={`mono text-[10px] font-bold px-2 py-0.5 rounded ${METHOD_COLORS[ep.method]}`}>{ep.method}</span>
                      </td>
                      <td><span className="mono text-xs">{ep.path}</span></td>
                      <td>
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold uppercase rounded border ${CATEGORY_COLORS[ep.category] || ""}`}>
                          {ep.category}
                        </span>
                      </td>
                      <td><Badge variant="outline" className="text-[10px]">{ep.scope_role}</Badge></td>
                      <td>
                        <Button size="sm" variant="ghost" onClick={() => copyToClipboard(curl, "cURL disalin")}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-key-dialog">
          <DialogHeader>
            <DialogTitle>Buat API Key Baru</DialogTitle>
            <DialogDescription>
              Setelah dibuat, raw key hanya ditampilkan satu kali. Pastikan menyalinnya ke tempat aman.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Nama (untuk identifikasi)</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Contoh: Core Banking Sync"
                data-testid="api-key-name-input"
              />
            </div>
            <div>
              <Label>Role / Permission Level</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="api-key-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Rate Limit (request/menit)</Label>
              <Input
                type="number" min={1} max={10000}
                value={form.rate_limit_per_min}
                onChange={(e) => setForm({ ...form, rate_limit_per_min: e.target.value })}
                className="mono"
                data-testid="api-key-rate-input"
              />
            </div>
            <div>
              <Label>Deskripsi (opsional)</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                placeholder="Sistem konsumen, owner, dll."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Batal</Button>
            <Button onClick={handleCreate} data-testid="api-key-submit">Buat Key</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Newly created key dialog (shown once) */}
      <Dialog open={!!newKey} onOpenChange={(o) => !o && setNewKey(null)}>
        <DialogContent data-testid="new-key-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5" /> API Key Berhasil Dibuat
            </DialogTitle>
            <DialogDescription className="text-[#D0021B]">
              ⚠ Salin sekarang. Setelah dialog ditutup, raw key tidak akan ditampilkan lagi.
            </DialogDescription>
          </DialogHeader>
          {newKey && (
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs uppercase tracking-wider">Raw Key</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input value={newKey.raw_key} readOnly className="mono text-xs" data-testid="new-key-value" />
                  <Button onClick={() => copyToClipboard(newKey.raw_key, "Key disalin")} data-testid="copy-new-key">
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Nama</Label>
                  <div className="font-medium">{newKey.name}</div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Role</Label>
                  <div><Badge variant="outline">{newKey.role}</Badge></div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Rate Limit</Label>
                  <div className="mono">{newKey.rate_limit_per_min}/min</div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Prefix</Label>
                  <div className="mono text-xs">{newKey.prefix}</div>
                </div>
              </div>
              <div className="border border-border rounded-md bg-zinc-50 p-3 text-xs">
                <div className="text-muted-foreground mb-1.5">Test koneksi:</div>
                <pre className="mono text-[11px] overflow-x-auto">{`curl -H "X-API-Key: ${newKey.raw_key}" \\
  ${window.location.origin}/api/dashboard/summary`}</pre>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setNewKey(null)} data-testid="close-new-key">Saya sudah menyimpan key</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
